import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  Database, Plus, Upload, FileText, DollarSign, Users, CheckCircle2,
  ArrowUpRight, X, Play, Pause, RefreshCw, TrendingUp, Sparkles, Receipt,
  ExternalLink, Copy, Palette, Save,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const portalUrl = (token) => `${window.location.origin}/reactivation-portal/${token}`;

const copyPortalLink = async (token) => {
  if (!token) return toast.error("No public token on this campaign yet.");
  try {
    await navigator.clipboard.writeText(portalUrl(token));
    toast.success("Public portal link copied.");
  } catch {
    toast.error("Copy failed — link: " + portalUrl(token));
  }
};

export default function Reactivation() {
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, cl] = await Promise.all([
        api.get("/reactivation/campaigns"),
        api.get("/clients"),
      ]);
      setCampaigns(c.data.campaigns || []);
      setClients(cl.data.clients || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generateInvoices = async () => {
    try {
      const { data } = await api.post("/reactivation/invoices/generate");
      toast.success(`Generated ${data.count} invoice(s).`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-10 max-w-7xl" data-testid="reactivation-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Database className="w-3.5 h-3.5" /> New service</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Database Reactivation</h1>
          <p className="text-neutral-500 max-w-2xl text-sm">
            Re-engage a client's old CRM leads with multi-touch SMS + voice sequences. You charge
            <strong className="text-neutral-900"> per booked appointment</strong> — no upfront cost to the client.
            Setup Fee tier available for larger imports ($500–$2,500).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={generateInvoices} className="lf-btn-outline text-sm inline-flex items-center gap-2" data-testid="reactivation-invoice-btn">
            <Receipt className="w-4 h-4" /> Generate pending invoices
          </button>
          <button onClick={() => setShowNew(true)} className="lf-btn-primary text-sm inline-flex items-center gap-2" data-testid="reactivation-new-btn">
            <Plus className="w-4 h-4" /> New campaign
          </button>
        </div>
      </div>

      {/* Aggregate KPIs */}
      <RollupKpis campaigns={campaigns} />

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-16 text-center bg-white">
          <Database className="w-8 h-8 mx-auto text-neutral-400 mb-3" />
          <div className="font-display text-2xl mb-2">No reactivation campaigns yet</div>
          <p className="text-neutral-500 max-w-lg mx-auto mb-5">
            Create a campaign for any client whose CRM you're going to work. Import their old leads, we'll re-engage them, and every booked appointment becomes a billable event.
          </p>
          <button onClick={() => setShowNew(true)} className="lf-btn-primary text-sm inline-flex items-center gap-2" data-testid="reactivation-empty-cta">
            <Plus className="w-4 h-4" /> Start first campaign
          </button>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
              <tr>
                <th className="text-left px-4 py-3">Campaign</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Leads</th>
                <th className="text-right px-4 py-3">Booked</th>
                <th className="text-right px-4 py-3">Pending invoice</th>
                <th className="text-right px-4 py-3">Rate</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`campaign-row-${c.id.slice(0,8)}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-neutral-400 font-mono">{c.invoice_cadence} · {money(c.price_per_appointment)}/appt</div>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/clients/${c.client_id}`} className="text-sm hover:underline">{c.client_business_name || "—"}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{c.leads_total || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-emerald-700 font-semibold">{c.appointments_total || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{c.pending_invoice || 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{money((c.appointments_total || 0) * (c.price_per_appointment || 0))}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => copyPortalLink(c.public_token)}
                        className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
                        title="Copy the public progress link (client-safe, no login)"
                        data-testid={`campaign-copy-portal-${c.id.slice(0,8)}`}
                      >
                        <Copy className="w-3 h-3" /> Portal
                      </button>
                      <button onClick={() => setSelected(c)} className="text-xs text-coral hover:underline inline-flex items-center gap-1" data-testid={`campaign-open-${c.id.slice(0,8)}`}>
                        Open <ArrowUpRight className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewCampaignModal clients={clients} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {selected && <CampaignDetailModal campaign={selected} onClose={() => setSelected(null)} onRefresh={load} />}
    </div>
  );
}

function RollupKpis({ campaigns }) {
  const totals = useMemo(() => {
    let leads = 0, booked = 0, pending = 0, active = 0, revenue = 0;
    campaigns.forEach((c) => {
      leads   += c.leads_total || 0;
      booked  += c.appointments_total || 0;
      pending += c.pending_invoice || 0;
      revenue += (c.appointments_total || 0) * (c.price_per_appointment || 0);
      if (c.status === "active") active += 1;
    });
    return { leads, booked, pending, active, revenue };
  }, [campaigns]);
  const tiles = [
    { label: "Active campaigns",    value: totals.active,        icon: Play,          accent: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { label: "Leads working",       value: totals.leads,         icon: Users,         accent: "text-blue-700 bg-blue-50 border-blue-200" },
    { label: "Appointments booked", value: totals.booked,        icon: CheckCircle2,  accent: "text-amber-700 bg-amber-50 border-amber-200" },
    { label: "Pending invoice",     value: totals.pending,       icon: Receipt,       accent: "text-violet-700 bg-violet-50 border-violet-200" },
    { label: "Revenue generated",   value: money(totals.revenue),icon: DollarSign,    accent: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
      {tiles.map((t) => (
        <div key={t.label} className={`border ${t.accent} p-4`} data-testid={`reactivation-kpi-${t.label.split(" ")[0].toLowerCase()}`}>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider opacity-70">
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </div>
          <div className="text-2xl font-bold mt-1.5">{t.value}</div>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    draft:     "bg-neutral-100 border-neutral-300 text-neutral-600",
    active:    "bg-emerald-50 border-emerald-300 text-emerald-800",
    paused:    "bg-amber-50 border-amber-300 text-amber-800",
    completed: "bg-blue-50 border-blue-300 text-blue-800",
  };
  return <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 border ${colors[status] || colors.draft}`}>{status}</span>;
}

function NewCampaignModal({ clients, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_id: "", name: "", price_per_appointment: 75, invoice_cadence: "weekly",
    setup_fee: 0, compliance_confirmed: false, notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Choose a client.");
    if (!form.name.trim()) return toast.error("Give the campaign a name.");
    if (!form.compliance_confirmed) return toast.error("Confirm the compliance checkbox to continue.");
    setBusy(true);
    try {
      await api.post("/reactivation/campaigns", {
        ...form,
        price_per_appointment: Number(form.price_per_appointment) || 75,
        setup_fee: Number(form.setup_fee) || 0,
      });
      toast.success("Campaign created.");
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="reactivation-new-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-lg border border-neutral-900 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-300">New reactivation campaign</div>
            <div className="text-xl font-bold mt-1">Set it up in 60 seconds</div>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Client *">
            <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="lf-input" data-testid="reactivation-client-select">
              <option value="">— Choose a client —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
            </select>
          </Field>
          <Field label="Campaign name *">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lf-input" placeholder="e.g. Summer 2026 CRM rebook" data-testid="reactivation-name-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="$ per appointment">
              <input type="number" min={1} value={form.price_per_appointment} onChange={(e) => setForm({ ...form, price_per_appointment: e.target.value })} className="lf-input" data-testid="reactivation-price-input" />
            </Field>
            <Field label="Invoice cadence">
              <select value={form.invoice_cadence} onChange={(e) => setForm({ ...form, invoice_cadence: e.target.value })} className="lf-input">
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
              </select>
            </Field>
          </div>
          <Field label="Setup fee (optional — for large / complex imports)">
            <input type="number" min={0} value={form.setup_fee} onChange={(e) => setForm({ ...form, setup_fee: e.target.value })} className="lf-input" placeholder="0" />
          </Field>
          <Field label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="lf-input text-sm" />
          </Field>
          <label className="flex items-start gap-2 border border-amber-300 bg-amber-50 p-3 cursor-pointer" data-testid="reactivation-compliance-check">
            <input type="checkbox" checked={form.compliance_confirmed} onChange={(e) => setForm({ ...form, compliance_confirmed: e.target.checked })} className="mt-0.5" />
            <div className="text-xs text-amber-900">
              <strong>Compliance:</strong> Client confirms these leads previously consented to contact and are their own customer data — not a purchased or scraped list.
            </div>
          </label>
        </div>
        <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lf-btn-outline text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="lf-btn-primary text-sm" data-testid="reactivation-create-submit">
            {busy ? "Creating…" : "Create campaign"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CampaignDetailModal({ campaign, onClose, onRefresh }) {
  const [tab, setTab] = useState("report");
  const [report, setReport] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [apptForm, setApptForm] = useState({ lead_name: "", service_type: "", appointment_at: "", notes: "" });
  const [appts, setAppts] = useState([]);
  const [theme, setTheme] = useState({
    brand_color: campaign.brand_color || "#F97316",
    logo_url: campaign.logo_url || "",
  });
  const [savingTheme, setSavingTheme] = useState(false);

  const loadAll = async () => {
    try {
      const [rep, ldz, app] = await Promise.all([
        api.get(`/reactivation/campaigns/${campaign.id}/report`),
        api.get(`/reactivation/campaigns/${campaign.id}/leads`),
        api.get(`/reactivation/campaigns/${campaign.id}/appointments`),
      ]);
      setReport(rep.data);
      setLeads(ldz.data.leads || []);
      setAppts(app.data.appointments || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [campaign.id]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a CSV first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/reactivation/campaigns/${campaign.id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.inserted} leads (${data.duplicates} dupes, ${data.invalid_phone} invalid phones).`);
      setFile(null);
      loadAll();
      onRefresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const logAppointment = async (e) => {
    e.preventDefault();
    if (!apptForm.lead_name.trim()) return toast.error("Enter a lead name.");
    try {
      await api.post("/reactivation/appointments", { ...apptForm, campaign_id: campaign.id });
      toast.success(`Appointment logged (+${money(campaign.price_per_appointment)}).`);
      setApptForm({ lead_name: "", service_type: "", appointment_at: "", notes: "" });
      loadAll();
      onRefresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleStatus = async () => {
    const next = campaign.status === "active" ? "paused" : "active";
    try {
      await api.patch(`/reactivation/campaigns/${campaign.id}`, { status: next });
      toast.success(`Campaign ${next}.`);
      onRefresh();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveTheme = async (e) => {
    e?.preventDefault();
    const color = (theme.brand_color || "").trim();
    if (color && !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return toast.error("Brand color must be a 6-digit hex like #E5533B.");
    }
    const logo = (theme.logo_url || "").trim();
    if (logo && !/^https?:\/\//i.test(logo)) {
      return toast.error("Logo URL must start with http:// or https://.");
    }
    setSavingTheme(true);
    try {
      await api.patch(`/reactivation/campaigns/${campaign.id}`, {
        brand_color: color || null,
        logo_url: logo || null,
      });
      toast.success("Portal theme saved.");
      onRefresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingTheme(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="reactivation-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-4xl border border-neutral-900 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-300">Campaign · {campaign.client_business_name}</div>
            <div className="text-xl font-bold mt-1">{campaign.name}</div>
            <div className="text-xs font-mono text-neutral-400 mt-1">{campaign.invoice_cadence} · {money(campaign.price_per_appointment)}/appt · {campaign.status}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleStatus} className="lf-btn-outline-white text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="campaign-toggle-status">
              {campaign.status === "active" ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Activate</>}
            </button>
            <button onClick={onClose} className="text-neutral-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="flex border-b border-neutral-200 bg-neutral-50">
          {[["report","Report"],["import","Import leads"],["leads","Leads"],["appointments","Log appointment"],["themes","Portal theme"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`campaign-tab-${k}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition ${tab === k ? "border-coral text-neutral-900 bg-white" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "report" && report && (
          <div className="p-6 space-y-4" data-testid="campaign-report-panel">
            <div className="grid grid-cols-4 gap-3">
              <MiniKpi label="Total leads"      value={report.leads_total} />
              <MiniKpi label="Contacted"        value={report.leads_contacted} />
              <MiniKpi label="Response rate"    value={`${report.response_rate_pct}%`} accent="amber" />
              <MiniKpi label="Booked appts"     value={report.appointments_total} accent="emerald" />
              <MiniKpi label="Pending invoice"  value={report.appointments_pending} accent="violet" />
              <MiniKpi label="Invoiced"         value={report.appointments_invoiced} />
              <MiniKpi label="Revenue $"        value={money(report.revenue_generated)} accent="emerald" />
              <MiniKpi label="$ / appt"         value={money(report.revenue_per_appointment)} />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Leads by status</div>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(report.leads_by_status || {}).map(([k, v]) => (
                  <div key={k} className="border border-neutral-200 p-2 text-xs" data-testid={`report-leads-${k}`}>
                    <div className="text-neutral-500 font-mono uppercase tracking-wider">{k.replace("_", " ")}</div>
                    <div className="font-bold text-lg">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "import" && (
          <div className="p-6" data-testid="campaign-import-panel">
            <div className="bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900 mb-4">
              CSV format: any headers matching <code>name, phone, email, last contact, inquired about, notes</code>. Duplicates, invalid phones, and stale contacts (&gt;2yr) are flagged automatically.
            </div>
            <form onSubmit={upload} className="flex flex-wrap items-end gap-3">
              <label className="flex-1">
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">CSV file</div>
                <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} className="lf-input" data-testid="campaign-csv-input" />
              </label>
              <button type="submit" disabled={uploading || !file} className="lf-btn-primary text-sm inline-flex items-center gap-2" data-testid="campaign-csv-upload">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Import"}
              </button>
            </form>
            {importResult && (
              <div className="mt-4 grid grid-cols-4 gap-2" data-testid="campaign-import-result">
                <MiniKpi label="Rows"            value={importResult.total_rows} />
                <MiniKpi label="Kept"            value={importResult.inserted}  accent="emerald" />
                <MiniKpi label="Duplicates"      value={importResult.duplicates} />
                <MiniKpi label="Invalid phones"  value={importResult.invalid_phone} accent="rose" />
                <MiniKpi label="Outside window"  value={importResult.outside_window} accent="amber" />
              </div>
            )}
          </div>
        )}

        {tab === "leads" && (
          <div className="p-6" data-testid="campaign-leads-panel">
            <div className="text-xs text-neutral-500 mb-3">{leads.length} lead(s) imported</div>
            <div className="border border-neutral-200 overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Phone</th>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Inquired about</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-neutral-400">Import a CSV to get started.</td></tr>}
                  {leads.slice(0, 200).map((l) => (
                    <tr key={l.id} className="border-t border-neutral-100">
                      <td className="px-3 py-2">{l.name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{l.phone}</td>
                      <td className="px-3 py-2 font-mono text-xs">{l.email}</td>
                      <td className="px-3 py-2 text-xs">{l.inquired_about}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-neutral-200 bg-white">{l.status}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-rose-600">{l.flag || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "themes" && (
          <div className="p-6" data-testid="campaign-themes-panel">
            <div className="flex items-start gap-2 mb-4 text-xs text-neutral-600 bg-amber-50 border border-amber-200 p-3">
              <Palette className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-amber-900">Client-branded portal</div>
                Set the client's brand color and logo. They apply to the public progress portal at{" "}
                <code className="text-[11px]">/reactivation-portal/{campaign.public_token || "…"}</code>. Leave a field blank to fall back to the default (coral + no logo).
              </div>
            </div>

            <form onSubmit={saveTheme} className="grid grid-cols-2 gap-4">
              <Field label="Brand color (6-digit hex)">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={/^#[0-9A-Fa-f]{6}$/.test(theme.brand_color) ? theme.brand_color : "#F97316"}
                    onChange={(e) => setTheme({ ...theme, brand_color: e.target.value.toUpperCase() })}
                    className="h-10 w-14 cursor-pointer border border-neutral-300"
                    data-testid="theme-brand-color-picker"
                    aria-label="Brand color picker"
                  />
                  <input
                    type="text"
                    value={theme.brand_color}
                    onChange={(e) => setTheme({ ...theme, brand_color: e.target.value })}
                    placeholder="#F97316"
                    className="lf-input font-mono flex-1"
                    data-testid="theme-brand-color-input"
                  />
                </div>
              </Field>

              <Field label="Logo URL (public https link)">
                <input
                  type="url"
                  value={theme.logo_url}
                  onChange={(e) => setTheme({ ...theme, logo_url: e.target.value })}
                  placeholder="https://cdn.example.com/logo.png"
                  className="lf-input"
                  data-testid="theme-logo-url-input"
                />
              </Field>

              {/* Live preview */}
              <div className="col-span-2 border border-neutral-200 bg-neutral-50 p-4">
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3">Live preview</div>
                <div className="bg-white border border-neutral-200 p-4 flex items-center gap-4">
                  {theme.logo_url ? (
                    <img
                      src={theme.logo_url}
                      alt="Logo preview"
                      className="w-16 h-16 object-contain border border-neutral-100 bg-neutral-50"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      data-testid="theme-logo-preview"
                    />
                  ) : (
                    <div className="w-16 h-16 border border-dashed border-neutral-300 flex items-center justify-center text-[10px] font-mono text-neutral-400 uppercase tracking-wider">
                      no logo
                    </div>
                  )}
                  <div className="flex-1">
                    <div
                      className="text-[11px] font-mono uppercase tracking-widest font-semibold"
                      style={{ color: /^#[0-9A-Fa-f]{6}$/.test(theme.brand_color) ? theme.brand_color : "#F97316" }}
                      data-testid="theme-preview-overline"
                    >
                      Reactivation dashboard
                    </div>
                    <div className="text-lg font-bold text-neutral-900 mt-0.5">{campaign.name}</div>
                    <div className="mt-2 h-2 bg-neutral-100 relative overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0"
                        style={{
                          width: "62%",
                          background: `linear-gradient(90deg, ${/^#[0-9A-Fa-f]{6}$/.test(theme.brand_color) ? theme.brand_color : "#F97316"} 0%, ${/^#[0-9A-Fa-f]{6}$/.test(theme.brand_color) ? theme.brand_color : "#F97316"}80 100%)`,
                        }}
                        data-testid="theme-preview-bar"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-2 flex items-center justify-between">
                {campaign.public_token ? (
                  <a
                    href={portalUrl(campaign.public_token)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1"
                    data-testid="theme-open-portal"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open live portal
                  </a>
                ) : (
                  <span className="text-xs text-neutral-400">No public token yet</span>
                )}
                <button
                  type="submit"
                  disabled={savingTheme}
                  className="lf-btn-primary text-sm inline-flex items-center gap-2"
                  data-testid="theme-save-btn"
                >
                  <Save className="w-4 h-4" /> {savingTheme ? "Saving…" : "Save theme"}
                </button>
              </div>
            </form>
          </div>
        )}

        {tab === "appointments" && (
          <div className="p-6" data-testid="campaign-appointments-panel">
            <form onSubmit={logAppointment} className="grid grid-cols-2 gap-3 mb-4">
              <Field label="Lead name *">
                <input required value={apptForm.lead_name} onChange={(e) => setApptForm({ ...apptForm, lead_name: e.target.value })} className="lf-input" data-testid="appt-lead-name" />
              </Field>
              <Field label="Service">
                <input value={apptForm.service_type} onChange={(e) => setApptForm({ ...apptForm, service_type: e.target.value })} className="lf-input" placeholder="e.g. Furnace tune-up" />
              </Field>
              <Field label="When (any format)">
                <input value={apptForm.appointment_at} onChange={(e) => setApptForm({ ...apptForm, appointment_at: e.target.value })} className="lf-input" placeholder="e.g. Tue 2pm" />
              </Field>
              <Field label="Notes">
                <input value={apptForm.notes} onChange={(e) => setApptForm({ ...apptForm, notes: e.target.value })} className="lf-input" />
              </Field>
              <div className="col-span-2 flex justify-end">
                <button type="submit" className="lf-btn-primary text-sm inline-flex items-center gap-2" data-testid="appt-log-btn">
                  <DollarSign className="w-4 h-4" /> Log appointment (+{money(campaign.price_per_appointment)})
                </button>
              </div>
            </form>
            <div className="text-xs text-neutral-500 mb-2">{appts.length} appointment(s) logged</div>
            <div className="border border-neutral-200 overflow-x-auto max-h-72">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
                  <tr>
                    <th className="text-left px-3 py-2">Lead</th>
                    <th className="text-left px-3 py-2">Service</th>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {appts.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-neutral-400">No appointments logged yet.</td></tr>}
                  {appts.map((a) => (
                    <tr key={a.id} className="border-t border-neutral-100">
                      <td className="px-3 py-2">{a.lead_name}</td>
                      <td className="px-3 py-2 text-xs">{a.service_type}</td>
                      <td className="px-3 py-2 text-xs">{a.appointment_at}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700 font-semibold">{money(a.amount)}</td>
                      <td className="px-3 py-2"><span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-neutral-200 bg-white">{a.status.replace("_", " ")}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function MiniKpi({ label, value, accent }) {
  const c = {
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    rose: "bg-rose-50 text-rose-900 border-rose-200",
    violet: "bg-violet-50 text-violet-900 border-violet-200",
  }[accent] || "bg-white text-neutral-900 border-neutral-200";
  return (
    <div className={`border ${c} p-2`}>
      <div className="text-xs font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
